import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Case } from 'dashboard/models/case';
import { SAR, SARFilingType, SARValidationErrors } from 'dashboard/models/sar';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setSAR,
  setSARCases,
  setSARErrorMessages,
  setSARValidationErrors,
} from 'dashboard/redux/slices/sar';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

export const loadSAR = (id: string): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    const response = await SessionClientDashboard.getResource<SAR>(ApiEndpoint.SAR, id);

    const sar = response.data;
    const errorMessages = response.meta?.messages || {};
    const cases = (response.included?.filter((resource) => resource.type === ApiModel.Case) ||
      []) as Case[];

    dispatch(setSAR(sar));
    dispatch(setSARErrorMessages({ sarToken: id, errorMessages }));
    dispatch(setSARCases(cases));
  };
};

// not all SAR attributes support updating with our API
interface SARUpdateAttributes {
  name?: string;
  'filing-type'?: SARFilingType;
  'bsa-id'?: string;
  data?: Record<string, unknown>;
}

export const updateSAR = (id: string, attributes: SARUpdateAttributes): DashboardThunkAction => {
  return async (dispatch) => {
    const { data: updatedSar } = await SessionClientDashboard.updateResource<SAR>({
      type: ApiModel.SAR,
      id,
      attributes,
    });

    dispatch(setSAR(updatedSar));
  };
};

export const exportSAR = (id: string) => {
  return async () => {
    const blob = await SessionClientDashboard.getBlob(ApiEndpoint.SAR, id, 'export');
    saveAs(blob, `${id}.xml`);
  };
};

export const autofillSAR = (id: string): DashboardThunkAction => {
  return async (dispatch) => {
    const { data: updatedSar } = await SessionClientDashboard.postResourceAction<SAR>(
      {
        type: ApiModel.SAR,
        id,
      },
      'autofill-attached-objects',
    );
    dispatch(setSAR(updatedSar));
  };
};

export const fileSAR = (id: string): DashboardThunkAction => {
  return async (dispatch) => {
    const { data: updatedSar } = await SessionClientDashboard.postResourceAction<SAR>(
      {
        type: ApiModel.SAR,
        id,
      },
      'file',
    );

    dispatch(setSAR(updatedSar));
  };
};

export const archiveSAR = (id: string): DashboardThunkAction => {
  return async (dispatch) => {
    const { data: updatedSar } = await SessionClientDashboard.postResourceAction<SAR>(
      {
        type: ApiModel.SAR,
        id,
      },
      'archive',
    );

    dispatch(setSAR(updatedSar));
  };
};

const parseServerValidationErrors = ({
  'xml-schema-errors': xmlSchemaErrors,
  'json-schema-errors': jsonSchemaErrors,
}: SARValidationErrors = {}) => {
  const parsedErrors: string[] = [];

  if (xmlSchemaErrors) {
    xmlSchemaErrors.forEach((error: string) => {
      const match = error.match(/(\d+:\d+): ERROR: (.+)/);
      if (match) {
        const [, _location, message] = match;
        parsedErrors.push(message);
      } else {
        parsedErrors.push(error);
      }
    });
  }

  if (jsonSchemaErrors) {
    jsonSchemaErrors.forEach((error: string) => {
      parsedErrors.push(error);
    });
  }

  return parsedErrors;
};

export const validateSAR = (id: string): DashboardThunkAction => {
  return async (dispatch) => {
    const response = await SessionClientDashboard.postResourceAction<
      SAR,
      {
        type: ApiModel.SAR;
        id: string;
      },
      {
        errors: SARValidationErrors;
      }
    >(
      {
        type: ApiModel.SAR,
        id,
      },
      'validate',
    );

    const validationErrors = parseServerValidationErrors(response.meta?.errors);
    dispatch(setSARValidationErrors({ sarToken: id, validationErrors }));

    if (validationErrors.length === 0) {
      dispatch(showMessage('The SAR passed all validations.', MessageType.Success));
    }
  };
};
